import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastSuccess, toastError } from '@/components/ui/use-toast';
import { PasswordStrengthIndicator } from '@/components/ui/PasswordStrengthIndicator';
import { useTranslation } from '@/hooks/useTranslation';
import { getCurrentLanguage } from '@/i18n';
import { TermsOfServiceModal } from '@/components/modals/TermsOfServiceModal';
import { API_BASE_URL } from '@/config';
import { PrivacyPolicyModal } from '@/components/modals/PrivacyPolicyModal';

export default function SignUp() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState<{valid: boolean; referrer_name?: string; error?: string} | null>(null);
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);

  const { } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract referral code from URL on component mount
  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam) {
      setReferralCode(refParam.toUpperCase());
      validateReferralCode(refParam.toUpperCase());
    }
  }, [searchParams]);

  // Referral code validation function
  const validateReferralCode = async (code: string) => {
    if (!code.trim()) {
      setReferralInfo(null);
      return;
    }

    setIsValidatingReferral(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/referrals/verify/${encodeURIComponent(code)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        setReferralInfo({
          valid: true,
          referrer_name: data.referrer_name
        });
      } else {
        setReferralInfo({
          valid: false,
          error: data.error || t('auth.register.errors.invalidReferralCode')
        });
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setReferralInfo({
        valid: false,
        error: t('auth.register.errors.referralValidationFailed')
      });
    } finally {
      setIsValidatingReferral(false);
    }
  };

  // Password validation function
  const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push(t('auth.register.validation.minLength'));
    }
    if (!/[A-Z]/.test(password)) {
      errors.push(t('auth.register.validation.uppercase'));
    }
    if (!/[a-z]/.test(password)) {
      errors.push(t('auth.register.validation.lowercase'));
    }
    if (!/\d/.test(password)) {
      errors.push(t('auth.register.validation.number'));
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push(t('auth.register.validation.specialChar'));
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      const redirect_uri = `${window.location.origin}/auth/callback`;
      console.log('Frontend redirect_uri:', redirect_uri);
      
      // Call the backend to get the Google OAuth URL (now optimized with hardcoded endpoints)
      const response = await fetch(`${API_BASE_URL}/api/auth/login/google`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          redirect_uri: redirect_uri
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('auth.register.errors.googleLoginFailed'));
      }

      const data = await response.json();

      // Redirect to Google's OAuth page (should be instant now with backend optimization)
      window.location.href = data.authorization_url;

    } catch (error) {
      console.error('Google login failed:', error);
      toastError({
        title: t('auth.register.errors.googleLoginFailedTitle'),
        description: error instanceof Error ? error.message : t('auth.register.errors.googleLoginFailed'),
      });
      setIsGoogleLoading(false);
    }
    // Note: Don't set isGoogleLoading to false on success as we're redirecting
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim()) {
      toastError({
        title: t('common.error'),
        description: t('auth.register.errors.fillAllFields'),
      });
      return;
    }

    // Validate password requirements
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      toastError({
        title: t('auth.register.errors.requirementsNotMet'),
        description: passwordValidation.errors.join('. '),
      });
      return;
    }

    if (password !== confirmPassword) {
      toastError({
        title: t('common.error'),
        description: t('auth.register.errors.passwordsDoNotMatch'),
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          password,
          referralCode: referralInfo?.valid ? referralCode : undefined,
          language_preference: getCurrentLanguage()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('auth.register.errors.registrationFailed'));
      }

      // Registration successful - show verification message
      setRegisteredEmail(email);
      setRegistrationComplete(true);
      toastSuccess({
        title: t('auth.register.success.title'),
        description: t('auth.register.success.description'),
      });

    } catch (error: any) {
      console.error('Registration failed:', error);
      let backendMsg = error.message || t('auth.register.errors.failedToCreateAccount');

      // Handle specific error cases
      if (error.message?.includes('already exists')) {
        toastError({
          title: t('auth.register.errors.emailAlreadyRegistered'),
          description: t('auth.register.errors.emailAlreadyRegisteredDescription'),
        });
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        toastError({
          title: t('auth.register.errors.connectionError'),
          description: t('auth.register.errors.connectionErrorDescription'),
        });
      } else if (error.message?.includes('referral')) {
        toastError({
          title: t('auth.register.errors.referralCodeError'),
          description: t('auth.register.errors.referralCodeErrorDescription'),
        });
      } else {
        toastError({
          title: t('auth.register.errors.registrationFailed'),
          description: backendMsg,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setIsResending(true);
    try {
      const response = await fetch('https://52.196.239.9:5000/api/auth/resend-verification', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: registeredEmail
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('auth.register.errors.failedToResendEmail'));
      }

      toastSuccess({
        title: t('auth.register.verification.emailSentTitle'),
        description: t('auth.register.verification.emailSentDescription'),
      });
    } catch (error: any) {
      console.error('Resend verification failed:', error);
      toastError({
        title: t('auth.register.errors.failedToResendEmailTitle'),
        description: error.message || t('auth.register.errors.tryAgainLater'),
      });
    } finally {
      setIsResending(false);
    }
  };

  // Show verification message after successful registration
  if (registrationComplete) {
    return (
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 px-4 sm:w-[350px] sm:px-0">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('auth.register.verification.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('auth.register.verification.description', { email: registeredEmail })}
          </p>
        </div>
        <div className="grid gap-6">
          <div className="grid gap-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">{t('auth.register.verification.nextSteps')}</h3>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>{t('auth.register.verification.step1')}</li>
                <li>{t('auth.register.verification.step2')}</li>
                <li>{t('auth.register.verification.step3')}</li>
              </ol>
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={isResending}
              onClick={handleResendVerification}
              className="w-full"
            >
              {isResending ? t('auth.register.verification.sending') : t('auth.register.verification.resendButton')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              {t('auth.register.verification.backToLogin')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col justify-center space-y-6 px-4 sm:w-[350px] sm:px-0">
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{t('auth.register.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('auth.register.subtitle')}
        </p>
      </div>
      <div className="grid gap-6">
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="firstName">{t('auth.register.firstName')}</Label>
              <Input
                id="firstName"
                placeholder={t('auth.register.placeholders.firstName')}
                autoCapitalize="words"
                autoComplete="given-name"
                disabled={isLoading}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lastName">{t('auth.register.lastName')}</Label>
              <Input
                id="lastName"
                placeholder={t('auth.register.placeholders.lastName')}
                autoCapitalize="words"
                autoComplete="family-name"
                disabled={isLoading}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t('auth.register.email')}</Label>
              <Input
                id="email"
                placeholder={t('auth.register.placeholders.email')}
                type="email"
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect="off"
                disabled={isLoading}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t('auth.register.password')}</Label>
              <Input
                id="password"
                placeholder={t('auth.register.placeholders.password')}
                type="password"
                autoComplete="new-password"
                disabled={isLoading}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {password && (
                <PasswordStrengthIndicator password={password} className="mt-2" />
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="confirmPassword">{t('auth.register.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                placeholder={t('auth.register.placeholders.confirmPassword')}
                type="password"
                autoComplete="new-password"
                disabled={isLoading}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>

            {/* Referral Code Section */}
            <div className="grid gap-2">
              <Label htmlFor="referralCode">{t('auth.register.referralCode')}</Label>
              <Input
                id="referralCode"
                placeholder={t('auth.register.placeholders.referralCode')}
                type="text"
                disabled={isLoading}
                value={referralCode}
                onChange={(e) => {
                  const code = e.target.value.toUpperCase();
                  setReferralCode(code);
                  if (code.trim()) {
                    validateReferralCode(code);
                  } else {
                    setReferralInfo(null);
                  }
                }}
              />

              {/* Referral Code Validation Display */}
              {isValidatingReferral && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary"></div>
                  {t('auth.register.referral.validating')}
                </div>
              )}

              {referralInfo && referralInfo.valid && (
                <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-700 dark:text-green-300">
                    {t('auth.register.referral.validCode', { referrer: referralInfo.referrer_name })}
                  </span>
                </div>
              )}

              {referralInfo && !referralInfo.valid && (
                <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-red-700 dark:text-red-300">
                    {referralInfo.error}
                  </span>
                </div>
              )}
            </div>
            <Button disabled={isLoading} className="w-full">
              {isLoading ? t('common.loading') : t('auth.register.createAccount')}
            </Button>
          </div>
        </form>
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t('auth.register.orContinueWith')}
            </span>
          </div>
        </div>
        <Button
          variant="outline"
          type="button"
          disabled={isLoading || isGoogleLoading}
          onClick={handleGoogleLogin}
          className="w-full flex items-center justify-center"
        >
          {isGoogleLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('common.loading')}...
            </>
          ) : (
            <>
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {t('auth.register.googleSignUp')}
            </>
          )}
        </Button>
        <p className="px-8 text-center text-sm text-muted-foreground">
          {t('auth.register.agreeTerms')}{' '}
          <button
            type="button"
            onClick={() => setShowTermsModal(true)}
            className="underline underline-offset-4 hover:text-primary text-sm"
          >
            {t('auth.register.termsOfService')}
          </button>{' '}
          {t('common.and')}{' '}
          <button
            type="button"
            onClick={() => setShowPrivacyModal(true)}
            className="underline underline-offset-4 hover:text-primary text-sm"
          >
            {t('auth.register.privacyPolicy')}
          </button>
          .
        </p>
        <p className="text-center text-sm">
          {t('auth.register.hasAccount')}{' '}
          <Link to="/login" className="underline underline-offset-4 hover:text-primary">
            {t('auth.register.signIn')}
          </Link>
        </p>
      </div>

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </div>
  );
}
